/**
 * SelectableConversationList Component
 * Renders a list of conversations with checkboxes for multi-selection.
 */

import PropTypes from 'prop-types';
import { isValidAvatar, getAvatarTestId } from '../utils/avatar';
import './SelectableConversationList.css';

const SelectableConversationItem = ({
  conversation,
  isSelected,
  onToggle,
  isDisabled = false,
}) => {
  const { id, name, title, avatars = [], roomId, disabledReason, isEncrypted = false } = conversation;
  const displayName = name || title || 'Unknown';
  const placeholderInitial = displayName?.charAt(0)?.toUpperCase() || '?';
  const avatarHash = roomId || id ? getAvatarTestId(roomId || id) : 'unknown';

  const handleClick = () => {
    if (isDisabled) return;
    onToggle(id || roomId);
  };

  const handleKeyDown = (e) => {
    if (isDisabled) return;
    if (e.key === 'Enter' || e.key === ' ') {
      e.preventDefault();
      onToggle(id || roomId);
    }
  };

  return (
    <div
      className={`selectable-item ${isSelected ? 'selected' : ''} ${isDisabled ? 'disabled' : ''}`}
      onClick={handleClick}
      onKeyDown={handleKeyDown}
      role="checkbox"
      aria-checked={isSelected}
      aria-disabled={isDisabled}
      tabIndex={isDisabled ? -1 : 0}
      title={isDisabled ? disabledReason : ''}
    >
      {/* Avatar */}
      <div className="selectable-item-avatars">
        {avatars.length === 1 && isValidAvatar(avatars[0]) ? (
          <img
            className="selectable-item-avatar-single"
            src={avatars[0]}
            alt=""
            crossOrigin="anonymous"
            data-testid={`avatar-forward-${avatarHash}`}
          />
        ) : avatars.length >= 2 && isValidAvatar(avatars[0]) && isValidAvatar(avatars[1]) ? (
          <>
            <img
              className="selectable-item-avatar-first"
              src={avatars[0]}
              alt=""
              crossOrigin="anonymous"
            />
            <img
              className="selectable-item-avatar-second"
              src={avatars[1]}
              alt=""
              crossOrigin="anonymous"
            />
          </>
        ) : avatars.length >= 2 && (isValidAvatar(avatars[0]) || isValidAvatar(avatars[1])) ? (
          <>
            {isValidAvatar(avatars[0]) ? (
              <img
                className="selectable-item-avatar-first"
                src={avatars[0]}
                alt=""
                crossOrigin="anonymous"
              />
            ) : (
              <div className="selectable-item-avatar-placeholder-first">
                {placeholderInitial}
              </div>
            )}
            {isValidAvatar(avatars[1]) ? (
              <img
                className="selectable-item-avatar-second"
                src={avatars[1]}
                alt=""
                crossOrigin="anonymous"
              />
            ) : (
              <div className="selectable-item-avatar-placeholder-second">
                {placeholderInitial}
              </div>
            )}
          </>
        ) : (
          <div className="selectable-item-avatar-placeholder">
            {placeholderInitial}
          </div>
        )}
        {isEncrypted && (
          <div className="selectable-item-lock-icon" title="Encrypted">
            <i className="fa fa-lock" />
          </div>
        )}
      </div>

      {/* Name */}
      <span className="selectable-item-name">{displayName}</span>

      {/* Checkbox (circle style on right) */}
      <div className={`selectable-item-checkbox ${isSelected ? 'checked' : ''}`}>
        {isSelected && <i className="fa fa-check selectable-item-checkmark" />}
      </div>
    </div>
  );
};

SelectableConversationItem.propTypes = {
  conversation: PropTypes.shape({
    id: PropTypes.string,
    roomId: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    avatars: PropTypes.arrayOf(PropTypes.string),
    disabledReason: PropTypes.string,
    isEncrypted: PropTypes.bool,
  }).isRequired,
  isSelected: PropTypes.bool.isRequired,
  onToggle: PropTypes.func.isRequired,
  isDisabled: PropTypes.bool,
};

const SelectableConversationList = ({
  conversations = [],
  selectedIds = [],
  onToggleSelection,
  disabledIds = [],
}) => {
  return (
    <div className="selectable-conversation-list">
      {conversations.map(conversation => {
        const conversationId = conversation.id || conversation.roomId;
        return (
          <SelectableConversationItem
            key={conversationId}
            conversation={conversation}
            isSelected={selectedIds.includes(conversationId)}
            onToggle={onToggleSelection}
            isDisabled={disabledIds.includes(conversationId)}
          />
        );
      })}
    </div>
  );
};

SelectableConversationList.propTypes = {
  conversations: PropTypes.arrayOf(PropTypes.shape({
    id: PropTypes.string,
    roomId: PropTypes.string,
    name: PropTypes.string,
    title: PropTypes.string,
    avatars: PropTypes.arrayOf(PropTypes.string),
  })),
  selectedIds: PropTypes.arrayOf(PropTypes.string),
  onToggleSelection: PropTypes.func.isRequired,
  disabledIds: PropTypes.arrayOf(PropTypes.string),
};

export default SelectableConversationList;
